import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { MiningRobotAI } from "../ai/miningRobotAI";
import { DamageType } from "../damageType";
import { randomInt } from "../random";
import { GoldDrop } from "./goldDrop";
import { Direction } from "../direction";
import { Hit } from "../effects/hit";
import { MovementEffect } from "../effects/movementEffect";
import {
	PRIORITY_DEFAULT,
	type ControllableByAI,
	type LookAtHorizontalDirection,
	type ParticleEmitter,
	type ProcessableThing,
} from "../interfaces";
import type { PhysicalThingWithHp } from "../physicalThingWithHp";
import { STD_BASE_HP } from "../physicalThingWithHp";
import type { Thing } from "../thing";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { ParticleType } from "../particleType";
import { MultiTileThingCore } from "./multitile/multiTileThingCore";
import { addDestroyEffectToBoard } from "./robotUtils";

export class MiningRobot
	extends MultiTileThingCore
	implements ProcessableThing, LookAtHorizontalDirection, ControllableByAI, ParticleEmitter
{
	static key = "MiningRobot";

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	getBaseGoldValue(): number {
		return getMobGoldValue(MiningRobot.key);
	}

	getParticleType(): ParticleType | null {
		return this.hp < this.maxHp ? ParticleType.DAMAGE_SMOKE : null;
	}

	getParticleIntensity(): number {
		return 1 - (this.hp / this.maxHp);
	}

	private lookDirection: Direction = Direction.E;
	private ai: MiningRobotAI;

	constructor() {
		super(true);
		this.hp = 100;
		this.maxHp = 100;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.ai = new MiningRobotAI(this);
		this.ai.probabilityToBreakBlockWhenStuck = 1;
		this.ai.alwaysKnowsWhereTargetIs = true;
	}

	processTurn(_board: Board): void {
		this.ai.processTurn();
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	getLookingAtHorizontalDirection(): Direction {
		return this.lookDirection;
	}

	isPassable(): boolean {
		return false;
	}

	getZOrder(): number {
		return ZOrder.MOB;
	}

	isHideUnderneath(): boolean {
		return false;
	}

	// ControllableByAI implementation
	getBoard(): Board {
		return this.board!;
	}

	setLookDirection(direction: Direction): void {
		this.lookDirection = direction;
	}

	getXY(): XY {
		return this.controller ? this.controller.getBottomLeftXY() : this.xy;
	}

	moveTo(target: XY): void {
		if (!this.controller) return;
		const delta = target.subtract(this.getXY());
		for (const part of this.controller.getParts()) {
			const partTargetXY = part.xy.add(delta);
			this.board!.addEffect(new MovementEffect(part, part.xy, partTargetXY));
			this.board!.moveThing(part, partTargetXY);
		}
		// Only update xy — don't use board.moveThing because the core
		// is not in any cell (added via addNonPhysicalThing).
		this.xy = this.xy.add(delta);
	}

	attack(target: PhysicalThingWithHp): void {
		this.board!.addEffect(new Hit(target.xy));
		target.applyDamage(DamageType.MELEE, STD_BASE_HP, this);
	}

	canMoveTo(target: XY): boolean {
		if (!this.controller) return false;
		const delta = target.subtract(this.getXY());
		const coreTarget = this.xy.add(delta);
		if (!this.canUseSingleXY(coreTarget)) return false;
		for (const part of this.controller.getParts()) {
			const partTarget = part.xy.add(delta);
			if (!this.canUseSingleXY(partTarget)) return false;
		}
		return true;
	}

	canAttack(target: PhysicalThingWithHp): boolean {
		return target != null && target.xy.isInSquareRadius(this.getXY(), 1);
	}

	protected onZeroHp(cause: Thing | null): void {
		if (this.board) {
			this.board.stats.recordMobKill(MiningRobot.key);
			if (cause) this.board.stats.recordKillByWeapon((cause.constructor as any).key);
		}
		addDestroyEffectToBoard(this.board!, this);
		const goldAmount = this.getBaseGoldValue();
		if (goldAmount > 0 && this.board) {
			GoldDrop.spawnOrMerge(this.board, this.xy, goldAmount);
		}
		super.onZeroHp(cause);
	}

	private canUseSingleXY(target: XY): boolean {
		if (!this.board!.isInBounds(target)) return false;
		if (this.xy.equals(target)) return true;
		if (this.controller) {
			for (const part of this.controller.getParts()) {
				if (part.xy.equals(target)) return true;
			}
		}
		if (this.board!.isPassable(target)) return true;
		return false;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			lookDirection: this.lookDirection,
		};
	}

	static deserialize(data: any): MiningRobot {
		const d = new MiningRobot();
		d.restoreCoreFields(data);
		d.lookDirection = data.lookDirection ?? Direction.E;
		return d;
	}
}
